<template>
  <div
    v-if="steps.length || live"
    class="ai-steps"
    :class="{ 'is-live': live }"
  >
    <button
      type="button"
      class="ai-steps__toggle"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="setOpen(!expanded)"
    >
      <span
        v-if="live"
        class="ai-spinner"
        aria-hidden="true"
      />
      <ai-icon
        v-else
        name="chevron"
        :size="14"
        class="ai-steps__chevron"
        :class="{ 'is-open': expanded }"
      />
      <span class="ai-steps__summary">{{ summary }}</span>
    </button>
    <ol
      v-if="expanded && steps.length"
      class="ai-steps__list"
    >
      <li
        v-for="step in steps"
        :key="step.id"
        class="ai-step"
        :class="`is-${step.status}`"
      >
        <span
          class="ai-step__state"
          :title="stateTitle(step)"
        >
          <span
            v-if="step.status === 'running'"
            class="ai-spinner ai-spinner--small"
          />
          <ai-icon
            v-else
            :name="stateIcon(step)"
            :size="14"
          />
        </span>
        <span class="ai-step__body">
          <span class="ai-step__title">{{ step.title }}</span>
          <span
            v-if="step.detail"
            class="ai-step__detail"
          >
            {{ step.detail }}
          </span>
        </span>
        <span
          v-if="step.durationMs >= 1000"
          class="ai-step__time"
        >
          {{ formatDuration(step.durationMs) }}
        </span>
      </li>
    </ol>
  </div>
</template>

<script>
  import AiIcon from './AiIcon.vue';
  import { count, duration } from './format';

  const STATE_ICONS = { done: 'check', cached: 'check', empty: 'check', error: 'alert', invalid: 'alert' };
  const STATE_TITLES = {
    done: 'Выполнено',
    cached: 'Данные уже были получены',
    empty: 'Выполнено, данных нет',
    error: 'Источник не ответил',
    invalid: 'Запрос отклонён: неверные параметры',
  };
  // Expanded/collapsed choice per message id for the page session: the timeline is re-created
  // whenever a conversation is reopened.
  const OPEN_STATE = new Map();

  export default {
    name: 'StepsTimeline',
    components: { AiIcon },
    props: {
      messageId: { type: [String, Number], default: null },
      steps: { type: Array, default: () => [] },
      live: { type: Boolean, default: false },
      status: { type: Object, default: null },
      progress: { type: Object, default: null },
      durationMs: { type: Number, default: null },
    },
    data() {
      return { open: OPEN_STATE.has(this.messageId) ? OPEN_STATE.get(this.messageId) : null };
    },
    computed: {
      expanded() {
        return this.open === null ? this.live : this.open;
      },
      tools() {
        return this.steps.filter((s) => s.kind === 'tool');
      },
      summary() {
        if (this.live) {
          const text = (this.status && this.status.text) || 'Обрабатываю запрос';
          const parts = [text];
          if (this.progress && this.progress.elapsed >= 3) parts.push(`${Math.round(this.progress.elapsed)} с`);
          return parts.join(' · ');
        }
        const parts = [`Ход работы: ${count(this.tools.length, ['запрос', 'запроса', 'запросов'])} к данным`];
        const failed = this.tools.filter((s) => s.status === 'error').length;
        if (failed) parts.push(`${failed} с ошибкой`);
        if (this.steps.some((s) => s.kind === 'compact')) parts.push('контекст сжат');
        if (this.durationMs) parts.push(duration(this.durationMs));
        return parts.join(' · ');
      },
    },
    watch: {
      live(value) {
        if (!value) this.setOpen(null);
      },
    },
    methods: {
      setOpen(value) {
        this.open = value;
        if (this.messageId == null) return;
        if (value === null) OPEN_STATE.delete(this.messageId);
        else OPEN_STATE.set(this.messageId, value);
      },
      stateIcon(step) {
        if (step.kind === 'compact') return 'collapse';
        if (step.kind === 'note') return 'info';
        return STATE_ICONS[step.status] || 'info';
      },
      stateTitle(step) {
        return STATE_TITLES[step.status] || '';
      },
      formatDuration(ms) {
        return duration(ms);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-steps {
    margin: 0 0 10px;
    font-size: 13px;
    color: var(--ui-text-muted);
  }

  .ai-steps__toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    min-height: 28px;
    padding: 3px 10px 3px 8px;
    border: 1px solid var(--ui-border) !important;
    border-radius: 999px !important;
    background: var(--ui-surface-soft);
    color: var(--ui-text-muted);
    font-size: 13px;
    text-align: left;

    &:hover {
      border-color: var(--ui-border-strong) !important;
      color: var(--ui-text);
    }
  }

  .ai-steps__summary {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ai-steps__chevron {
    flex: 0 0 auto;
    transition: transform 0.16s ease;

    &.is-open {
      transform: rotate(90deg);
    }
  }

  .ai-steps__list {
    margin: 8px 0 0 13px;
    padding: 0 0 0 14px;
    list-style: none;
    border-left: 1px solid var(--ui-border);
  }

  .ai-step {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 4px 0;
  }

  .ai-step__state {
    display: inline-flex;
    flex: 0 0 18px;
    justify-content: center;
    margin: 1px 0 0 -24px;
    padding: 1px;
    border-radius: 50%;
    background: var(--ui-surface);
    color: var(--ui-success);
  }

  .is-empty .ai-step__state,
  .is-cached .ai-step__state {
    color: var(--ui-text-muted);
  }

  .is-error .ai-step__state {
    color: var(--ui-danger);
  }

  .is-invalid .ai-step__state {
    color: var(--ui-warning);
  }

  .is-running .ai-step__state {
    color: var(--ui-primary);
  }

  .ai-step__body {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: 2px 8px;
    min-width: 0;
  }

  .ai-step__title {
    color: var(--ui-text);
    overflow-wrap: anywhere;
  }

  .ai-step__detail {
    color: var(--ui-text-muted);
  }

  .ai-step__time {
    flex: 0 0 auto;
    color: var(--ui-text-muted);
    font-size: 12px;
  }
</style>
